import {combineEpics, Epic} from 'redux-observable';
import {EMPTY, from, of} from 'rxjs';
import {catchError, filter, map, startWith, switchMap,} from 'rxjs/operators';
import {isOfType} from 'typesafe-actions';

import {
  ILoadSelectedSharingSettingsAction,
  ILoadSharingSettingsAction,
  IShareWithGroupAction,
  IShareWithUserAction,
  IShareWithWorkspaceAction,
  loadedSelectedSharingSettings,
  loadedSharingSettings,
  loadingSelectedSharingSettings,
  loadingSelectedSharingSettingsFailed,
  loadingSharingSettings,
  loadingSharingSettingsFailed,
  sharedWithGroup,
  sharedWithUser,
  sharedWithWorkspace,
  SharingSettingsActions,
  SharingSettingsActionsTypes,
  sharingWithGroup,
  sharingWithGroupFailed,
  sharingWithUser,
  sharingWithUserFailed,
  sharingWithWorkspace,
  sharingWithWorkspaceFailed,
} from '../actions/sharingSettingsActions';
import {
  getDatabaseSharingSettings,
  getSelectedDatabaseSharingSettings,
  shareDatabaseWithUser,
  shareDatabaseWithGroup,
  shareDatabaseWithWorkspace,
} from '../../services/databasesApi';
import {
  getIntegrationSharingSettings,
  shareIntegrationWithUser,
  shareIntegrationWithGroup,
  shareIntegrationWithWorkspace,
} from '../../services/integrationApi';
import {
  getReportSharingSettings,
  shareReportWithUser,
  shareReportWithGroup,
  shareReportWithWorkspace,
} from '../../services/reportApi';
import {SharingSettingsTypes,} from '../../models';
import {IState} from '../reducers';

const _loadSharingSettings = (action: ILoadSharingSettingsAction, callFn: Function) => {
  return from(callFn(action.payload.uid))
    .pipe(
      map((response: any) => loadedSharingSettings(action.payload.type, action.payload.uid, response.result)),
      startWith(loadingSharingSettings(action.payload.type)),
      catchError(() => of(loadingSharingSettingsFailed(action.payload.type)))
    );
};

const loadSharingSettingsEpic: Epic<SharingSettingsActions, SharingSettingsActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(SharingSettingsActionsTypes.LOAD_SHARING_SETTINGS)),
    switchMap((action: ILoadSharingSettingsAction) => {
      switch (action.payload.type) {
        case SharingSettingsTypes.DATABASE:
          return _loadSharingSettings(action, getDatabaseSharingSettings);
        case SharingSettingsTypes.REPORT:
          return _loadSharingSettings(action, getReportSharingSettings);
        case SharingSettingsTypes.INTEGRATION:
          return _loadSharingSettings(action, getIntegrationSharingSettings);
        default:
          return EMPTY;
      }
    })
  );

const loadSelectedDatabaseSharingSettings = (action: ILoadSelectedSharingSettingsAction) => {
  return from(getSelectedDatabaseSharingSettings(action.payload.uidArr))
    .pipe(
      map((response: any) => loadedSelectedSharingSettings(action.payload.type, response)),
      startWith(loadingSelectedSharingSettings()),
      catchError(() => of(loadingSelectedSharingSettingsFailed()))
    );
};

const loadSelectedSharingSettingsEpic: Epic<SharingSettingsActions, SharingSettingsActions, IState, any> = (
  action$: any,
  state: any,
  { dispatch }: any,
) => {
  return action$.pipe(
    filter(isOfType(SharingSettingsActionsTypes.LOAD_SELECTED_SHARING_SETTINGS)),
    switchMap((action: any) => {
      switch (action.payload.type) {
        case SharingSettingsTypes.DATABASE:
          return loadSelectedDatabaseSharingSettings(action);
        default:
          return EMPTY;
      }
    })
  )
}

const _shareWithUser = (action: IShareWithUserAction, callFn: Function) => {
  return from(callFn(action.payload.uid, action.payload.userUid, action.payload.permission))
    .pipe(
      map((response: any) => sharedWithUser(action.payload.type, action.payload.uid, response.result)),
      startWith(sharingWithUser()),
      catchError(() => of(sharingWithUserFailed())),
    );
};

const shareWithUserEpic: Epic<SharingSettingsActions, SharingSettingsActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(SharingSettingsActionsTypes.SHARE_WITH_USER)),
    switchMap((action: IShareWithUserAction) => {
      // @TODO - merge SharingSettingsTypes as only API request differs
      switch (action.payload.type) {
        case SharingSettingsTypes.DATABASE:
          return _shareWithUser(action, shareDatabaseWithUser);
        case SharingSettingsTypes.REPORT:
          return _shareWithUser(action, shareReportWithUser);
        case SharingSettingsTypes.INTEGRATION:
          return _shareWithUser(action, shareIntegrationWithUser);
        default:
          return EMPTY;
      }
    })
  );

const _shareWithGroup = (action: IShareWithGroupAction, callFn: Function) => {
  return from(callFn(action.payload.uid, action.payload.groupUid, action.payload.permission))
    .pipe(
      map((response: any) => sharedWithGroup(action.payload.type, action.payload.uid, response.result)),
      startWith(sharingWithGroup()),
      catchError(() => of(sharingWithGroupFailed())),
    );
};

const shareWithGroupEpic: Epic<SharingSettingsActions, SharingSettingsActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(SharingSettingsActionsTypes.SHARE_WITH_GROUP)),
    switchMap((action: IShareWithGroupAction) => {
      switch (action.payload.type) {
        case SharingSettingsTypes.DATABASE:
          return _shareWithGroup(action, shareDatabaseWithGroup);
        case SharingSettingsTypes.REPORT:
          return _shareWithGroup(action, shareReportWithGroup);
        case SharingSettingsTypes.INTEGRATION:
          return _shareWithGroup(action, shareIntegrationWithGroup);
        default:
          return EMPTY;
      }
    })
  );

const _shareWithWorkspace = (action: IShareWithWorkspaceAction, callFn: Function) => {
  return from(callFn(action.payload.uid, action.payload.permission)).pipe(
    map((response: any) => sharedWithWorkspace(action.payload.type, action.payload.uid, response.result)),
    startWith(sharingWithWorkspace()),
    catchError(() => of(sharingWithWorkspaceFailed()))
  );
};

const shareWithWorkspaceEpic: Epic<SharingSettingsActions, SharingSettingsActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(SharingSettingsActionsTypes.SHARE_WITH_WORKSPACE)),
    switchMap((action: IShareWithWorkspaceAction) => {
      // @TODO - merge SharingSettingsTypes as only API request differs
      switch (action.payload.type) {
        case SharingSettingsTypes.DATABASE:
          return _shareWithWorkspace(action, shareDatabaseWithWorkspace);
        case SharingSettingsTypes.REPORT:
          return _shareWithWorkspace(action, shareReportWithWorkspace);
        case SharingSettingsTypes.INTEGRATION:
          return _shareWithWorkspace(action, shareIntegrationWithWorkspace);
        default:
          return EMPTY;
      }
    })
  );

export default combineEpics(
  loadSharingSettingsEpic,
  loadSelectedSharingSettingsEpic,
  shareWithUserEpic,
  shareWithGroupEpic,
  shareWithWorkspaceEpic,
);
